<template>
  <ReportingLayout>
    <div style="margin: 15px">
      <LocaleChanger
        class="noprint"
        :leftAlign="true"
        v-bind:emitRefresh="false"
        :key="'locale' + localeKey"
      />

      <Logo/>

      <h2>{{ $t("report") }}</h2>
      <br />

      <h4>{{ $store.state.main.game.name }}</h4>
      <br />
      <h4>{{ $t("date") }} {{ openDate }}</h4>
      <br />

      <base-button
        class="noprint"
        type="secondary"
        style="margin: 2px"
        v-on:click="openPrintDialog()"
        >{{ $t("print") }}</base-button
      >
      <br />
      <base-button
        v-if="
          $store.getters.getIsFacilitator &&
          !this.$store.state.main.playerIsObserver
        "
        class="noprint"
        type="secondary"
        style="margin: 2px"
        v-on:click="exportToExcel()"
        >{{ $t("excel") }}</base-button
      >
      <br />
      <br />
      <NetworkError />
      <div
        v-if="
          !$store.state.main.playerIsOnlyFacilitator &&
          !$store.state.main.playerIsObserver
        "
        class="noprint"
      >
        {{ $t("reporthasbeenmailed") }}
        <br />
        <br />
      </div>
      <br/>
      <ActionPlan v-if="dataReady"/>
      <br/>
      <br/>

      <div v-if="$store.getters.getIsFacilitator">
        <base-dropdown>
          <base-button slot="title" type="secondary" class="dropdown-toggle">
            {{ dropdownText }}
          </base-button>
          <div>
            <a class="dropdown-item" @click="showAll()">
              {{ $t("teamreport") }}
            </a>
          </div>
          <div v-for="player in $store.getters.getPlayers" :key="player.id">
            <a class="dropdown-item" @click="showSomePlayer(player.id)">
              {{ player.nick_name }}
            </a>
          </div>
        </base-dropdown>
        <br/>
      </div>

      <br/>
     
      <div class="container ml-0" v-if="dataReady">
        <span v-for="rpsCase in $store.getters.getRPSCasesInCurrentLocale" :key="rpsCase.id">
          <div class="row">
            <div class="col">
              <h5 style="font-weight: 900;">{{ rpsCase.caseNumber }}. {{ rpsCase.title }}</h5>
              <h6 style="margin-bottom: 15px;">{{ rpsCase.caseQuestion }}</h6>
            </div>
          </div>

          <div v-if="rpsCase.descriptionAboveCases">
            <span class="font-weight-bold">{{ rpsCase.descriptionAboveCases }}</span>
          </div>

          <div
            :style="getStyle(index)"
            class="row border-bottom"
            v-for="(
              rpsCaseChoices, index
            ) in $store.getters.getRPSCaseChoicesOfRPSCaseInCurrentLocale(
              rpsCase.rpsCase
            )"
            :key="rpsCaseChoices.id || index"
          >
            <div class="col">
              <h6 class="mb-0 font-weight-normal">
                <span style="font-size: 0.95rem;" v-html="rpsCaseChoices.choice_text" />
              </h6>
            </div>
            <div class="col">
              <Answers
                :selectedCaseNumber="rpsCase.caseNumber"
                :selectedCaseChoice="rpsCaseChoices"
                :selectedPlayer="selectedPlayer"
                :sortPlayerNames="true"
                reporting=true
              />
            </div>
          </div>
          <div v-if="rpsCase.descriptionBelowCases">
            <span class="font-weight-bold">{{ rpsCase.descriptionBelowCases }}</span>
          </div>
          <br/>
        </span>
      </div>

      <Suggestions v-if="dataReady"/>

      <br/>
      <br />
      <div class="noprint">
        <Feedback />
        <br />
        <br />
        <div v-if="$store.getters.getIsFacilitator">
          <hr/>
          <FacilitatorFeedback/>
          <br />
          <br />            
        </div>
      </div>

      <div class="noprint">
        <FacilitatorTools
          v-if="
            $store.getters.getIsFacilitator &&
            !$store.state.main.playerIsObserver
          "
        ></FacilitatorTools>
        <br />
      </div>
    </div>
  </ReportingLayout>
</template>

<script>
import ReportingLayout from "@/layouts/ReportingLayout.vue";
import LocaleChanger from "@/components/LocaleChanger.vue";
import Answers from "@/components/rps/Answers.vue";
import NetworkError from "@/components/NetworkError.vue";
import Feedback from "@/components/Feedback.vue";
import FacilitatorTools from "@/components/FacilitatorTools.vue";
import FacilitatorFeedback from "@/components/FacilitatorFeedback.vue";
import {
  getAndCommitGameByGameCode,
  getAndCommitRPSScenario,
  getAndCommitRPSCases,
  getAndCommitPlayersByGame,
  getAndCommitRPSCaseChoices,
  getAndCommitRPSAnswersByGame,
  getGameByReportingCode,
  getPlayerByReportingCode,
  getAndCommitSuggestionsByGame,
  getThisAdminInformation,
  getPlayerByFacilitatorId,  
} from "@/services/APIService";
import { generateExcelExportForRPSReport } from "@/services/ExcelExport";
import Logo from "@/components/Logo.vue";
import Suggestions from "@/components/charts/Suggestions.vue";
import ActionPlan from "@/components/ActionPlan/ActionPlan.vue";

export default {
  components: {
    ReportingLayout,
    LocaleChanger,
    NetworkError,
    Feedback,
    FacilitatorTools,
    Answers,
    FacilitatorFeedback,
    Logo,
    Suggestions,
    ActionPlan,
  },

  data: function () {
    return {
      localeKey: 0,
      selectedPlayer: 0,
      dataReady: false,
    };
  },
  computed: {
    openDate() {
      return new Date(this.$store.state.main.game.opened).toLocaleDateString(
        this.$i18n.locale
      );
    },
    dropdownText() {
      if (!this.selectedPlayer) {
        return this.$t("teamreport")
      } else {
        return this.$store.getters.getPlayerById(this.selectedPlayer).nick_name;
      }
    },
  },
  methods: {
    async updateData() {
      // Read data to Vuex
      await getAndCommitGameByGameCode(
        this.$store.state.main.game.gameCode,
        this.$store.commit
      );

      await this.$store.dispatch("updateActionPlanVotes")

      const p1 = getAndCommitRPSScenario(
        this.$store.state.main.game.rpsScenario,
        this.$store.state.main.game.gameCode,
        this.$store.commit
      );

      const p2 = getAndCommitRPSCases(
        this.$store.state.main.game.rpsScenario,
        this.$store.state.main.game.gameCode,
        this.$store.commit
      );

      const p3 = getAndCommitPlayersByGame(
        this.$store.state.main.game.id,
        this.$store.state.main.game.gameCode,
        this.$store.commit
      );

      const p4 = getAndCommitRPSCaseChoices(
        this.$store.state.main.game.rpsScenario,
        this.$store.state.main.game.gameCode,
        this.$store.commit
      );

      const p5 = getAndCommitRPSAnswersByGame(
        this.$store.state.main.game.id,
        this.$store.state.main.game.gameCode,
        this.$store.commit
      );

      const p6 = getAndCommitSuggestionsByGame(
        this.$store.state.main.game.id,
        this.$store.state.main.game.gameCode,
        this.$store.commit
      );

      Promise.all([p1, p2, p3, p4, p5, p6]).then((resp) => {
        this.$store.commit("setGameLocales");
        this.$store.commit({
          type: "setSelectedLocale",
          selectedLocale: this.$store.state.main.game.defaultLocale,
        });

        this.$i18n.locale = this.$store.state.main.game.defaultLocale;

        this.localeKey++;

        this.dataReady = true;
      })
    },
    openPrintDialog() {
      window.print();
    },
    exportToExcel() {
      generateExcelExportForRPSReport(this.$store);
    },
    getStyle(index) {
      if (index % 2 === 0) {
        return "background-color: rgba(51,68,109,.03);";
      } else {
        return "";
      }
    },
    showAll() {
      this.selectedPlayer = 0;
    },
    showOwn() {
      this.selectedPlayer = this.$store.state.main.thisPlayerId;
    },
    showSomePlayer(id) {
      this.selectedPlayer = id;
    },
  },
  async created() {
    if (this.$route.params.reportingcode) {
      // Personal reporting code included in URL
      this.reportingCodeReceived = true;

      const reportingCode = this.$route.params.reportingcode;
      const resp = await getGameByReportingCode(reportingCode);

      if (resp.game_code) {
        // Correct code
        this.$store.commit({
          type: "setThisGameCode",
          gameCode: resp.game_code,
        });

        this.$store.commit({
          type: "setThisGameId",
          gameCode: resp.id,
        });

        // We also need to get the player id
        const playerResp = await getPlayerByReportingCode(reportingCode);

        this.$store.commit({
          type: "setThisPlayerId",
          playerId: playerResp.id,
        });
      } else {
        // Wrong reporting code
        this.$router.push("/");
      }
    }

    if (
      this.$route.params.gamecode &&
      this.$store.getters.getIsFacilitator &&
      !this.$store.state.main.playerIsObserver
    ) {
      this.$store.commit("reinitializeStore");
      this.gameCodeReceived = true;

      // Only facilitator can show the reporting with gamecode in URL
      this.$store.commit({
        type: "setThisGameCode",
        gameCode: this.$route.params.gamecode,
      });
    } else if (!this.$store.state.main.game.gameCode) {
      // Not allowed to show
      this.$router.push("/");
    }

    // Let's set thisPlayerId if facilitator
    // has opened to report from game list.
    if (
      this.$store.getters.getIsFacilitator &&
      !this.$store.state.main.thisPlayerId
    ) {
      // Get facilitator information and fill fields
      getThisAdminInformation().then((resp) => {
        // Let's first check if the facilitator has already played this game...
        getPlayerByFacilitatorId(
          this.$store.state.main.game.gameCode,
          resp.id
        ).then((player) => {
          if (player) {
            this.$store.dispatch(
              "log",
              "Existing facilitator player found: " + player.id
            );
            
            this.$store.commit({
              type: "setThisPlayerId",
              playerId: player.id,
            });
          }
        })
      })
    }


    this.$store.dispatch("connectNotification");
    this.$store.dispatch("updateGame");

    this.updateData();
  },
};
</script>

<i18n>
{
  "en": {
    "report": "Report",
    "name": "Name:",
    "date": "Date:",
    "reporthasbeenmailed": "A link to this report has been sent to you via email.",
    "print": "Print...",
    "excel": "Export to Excel",
    "teamreport": "Team report"
  },
  "fi": {
    "report": "Raportti",
    "name": "Nimi:",
    "date": "Päiväys:",
    "reporthasbeenmailed": "Linkki tähän raporttiin on lähetetty sinulle sähköpostilla.",
    "print": "Tulosta...",
    "excel": "Vie Exceliin",
    "teamreport": "Tiimiraportti"
  },
  "sv": {
    "report": "Rapporterna",
    "name": "Namn:",
    "date": "Datum:",
    "reporthasbeenmailed": "En länk till denna rapport har skickats till dig via e-post.",
    "print": "Skriv ut...",
    "excel": "Exportera till Excel",
    "teamreport": "Teamrapport"
  },
  "de": {
    "report": "Berichte",
    "name": "Name:",
    "date": "Datum:",
    "reporthasbeenmailed": "Ein Link zu diesem Bericht wurde Ihnen per E-Mail gesendet.",
    "print": "Drucken...",
    "excel": "In Excel exportieren",
    "teamreport": "Teamreport"    
  },
  "ko": {
    "report": "결과보고서",
    "name": "이름:",
    "date": "날짜:",
    "reporthasbeenmailed": "개별 결과보고서를 확인할 수 있는 링크가 이메일로 전송되었습니다.",
    "print": "인쇄",
    "excel": "엑셀파일로 다운받기",
    "teamreport": "팀 보고서"      
  }
}
</i18n>
<style scoped>
@media print {
  @page {
    margin: 1cm;
  }
  body {
    width: 210mm;
    height: 297mm;
    margin: 25mm 25mm 25mm 25mm;
    -webkit-print-color-adjust: exact;
  }
  .noprint {
    display: none !important;
  }
  .card {
    display: none !important;
  } /* hide facilitator toolbox */
}
</style>
